import React, { useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { feedbackService } from '../services/feedbackService';
import { getUserEmail, loginRequest } from './auth/authConfig';
import { AuthError } from '../utils/apiUtils';
import './FeedbackButton.css';

const FeedbackButton = () => {
    const { instance, accounts } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const [feedback, setFeedback] = useState({
        rating: null, // 'positive' or 'negative'
        category: '',
        text: '',
        context: window.location.pathname
    });

    const categories = [
        'Bug Report',
        'Feature Request',
        'User Experience',
        'Performance',
        'Content Quality',
        'General Feedback'
    ];

    const handleSignIn = async () => {
        try {
            await instance.loginRedirect(loginRequest);
        } catch (err) {
            console.error('Sign-in redirect failed:', err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isAuthenticated) {
            return;
        }

        if (!feedback.rating || !feedback.category) {
            setError('Please provide a rating and select a category');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            const feedbackData = {
                rating: feedback.rating,
                category: feedback.category,
                text: feedback.text || null,
                context: feedback.context,
                userAgent: navigator.userAgent,
                timestamp: new Date().toISOString(),
                url: window.location.href,
                screenResolution: `${window.screen.width}x${window.screen.height}`
            };

            await feedbackService.submitFeedback(feedbackData);

            setSubmitted(true);
            setTimeout(() => {
                setIsModalOpen(false);
                setSubmitted(false);
                setFeedback({
                    rating: null,
                    category: '',
                    text: '',
                    context: window.location.pathname
                });
            }, 2000);
        } catch (err) {
            console.error('Error submitting feedback:', err);
            if (err?.type === AuthError.NO_ACCOUNT || err?.type === AuthError.TOKEN_EXPIRED) {
                setError('Please sign in to submit feedback.');
            } else {
                setError('Failed to submit feedback. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setIsModalOpen(false);
        setError('');
        setSubmitted(false);
        setFeedback({
            rating: null,
            category: '',
            text: '',
            context: window.location.pathname
        });
    };

    return (
        <>
            <button
                className="feedback-floating-button"
                onClick={() => setIsModalOpen(true)}
                title="Send Feedback"
            >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                    <g strokeWidth={0} />
                    <g strokeLinecap="round" strokeLinejoin="round" />
                    <g>
                        <path
                            d="M12 15V12M12 12V9M12 12H9M12 12H15M12.0001 21C10.365 21 8.83174 20.5639 7.51025 19.8018C7.3797 19.7265 7.31434 19.6888 7.25293 19.6719C7.19578 19.6561 7.14475 19.6507 7.08559 19.6548C7.02253 19.6591 6.9573 19.6808 6.82759 19.7241L4.51807 20.4939L4.51625 20.4947C4.02892 20.6572 3.7848 20.7386 3.62256 20.6807C3.4812 20.6303 3.36979 20.5187 3.31938 20.3774C3.26157 20.2152 3.34268 19.9719 3.50489 19.4853L3.50586 19.4823L4.27468 17.1758L4.27651 17.171C4.31936 17.0424 4.34106 16.9773 4.34535 16.9146C4.3494 16.8554 4.34401 16.804 4.32821 16.7469C4.31146 16.6863 4.27448 16.6221 4.20114 16.495L4.19819 16.4899C3.43604 15.1684 3 13.6351 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9707 21 12.0001 21Z"
                            stroke="#1b1b1b"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </g>
                </svg>
            </button>

            {isModalOpen && (
                <div className="feedback-modal-overlay" onClick={handleClose}>
                    <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="feedback-modal-header">
                            <h3>Send Feedback</h3>
                            <button
                                type="button"
                                className="feedback-close-button"
                                onClick={handleClose}
                            >
                                ×
                            </button>
                        </div>

                        {submitted ? (
                            <div className="feedback-success">
                                <div className="feedback-success-icon">✓</div>
                                <h4>Thank you for your feedback!</h4>
                                <p>Your feedback helps us improve the application.</p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="feedback-form">
                                {error && (
                                    <div className="feedback-error" role="alert">
                                        {error}
                                    </div>
                                )}

                                <div
                                    className={
                                        isAuthenticated
                                            ? 'feedback-form-locked'
                                            : 'feedback-form-locked feedback-form-locked--inactive'
                                    }
                                >
                                    <fieldset
                                        className="feedback-form-fieldset"
                                        disabled={!isAuthenticated}
                                    >
                                        <div className="feedback-section">
                                            <div className="feedback-label">How was your experience?</div>
                                            <div className="feedback-rating">
                                                <button
                                                    type="button"
                                                    className={`feedback-rating-button ${feedback.rating === 'positive' ? 'selected positive' : ''}`}
                                                    onClick={() => setFeedback({ ...feedback, rating: 'positive' })}
                                                >
                                                    👍 Good
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`feedback-rating-button ${feedback.rating === 'negative' ? 'selected negative' : ''}`}
                                                    onClick={() => setFeedback({ ...feedback, rating: 'negative' })}
                                                >
                                                    👎 Not Good
                                                </button>
                                            </div>
                                        </div>

                                        <div className="feedback-section">
                                            <label className="feedback-label" htmlFor="feedback-category">
                                                Category
                                            </label>
                                            <select
                                                id="feedback-category"
                                                value={feedback.category}
                                                onChange={(e) => setFeedback({ ...feedback, category: e.target.value })}
                                                className="feedback-select"
                                                required
                                            >
                                                <option value="">Select category...</option>
                                                {categories.map((category) => (
                                                    <option key={category} value={category}>
                                                        {category}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="feedback-section">
                                            <label className="feedback-label" htmlFor="feedback-comments">
                                                Additional comments (optional)
                                            </label>
                                            <textarea
                                                id="feedback-comments"
                                                value={feedback.text}
                                                onChange={(e) => setFeedback({ ...feedback, text: e.target.value })}
                                                placeholder="Tell us more about your experience..."
                                                className="feedback-textarea"
                                                rows={4}
                                            />
                                        </div>

                                        <div className="feedback-auth-info">
                                            {accounts.length > 0 && (
                                                <p className="feedback-user-info">
                                                    Submitting as: {getUserEmail(accounts[0]) || accounts[0].name}
                                                </p>
                                            )}
                                        </div>
                                    </fieldset>

                                    {!isAuthenticated && (
                                        <div
                                            className="feedback-signin-overlay"
                                            role="region"
                                            aria-label="Sign in to submit feedback"
                                        >
                                            <p className="feedback-signin-message">Sign in to send feedback.</p>
                                            <button
                                                type="button"
                                                className="feedback-signin-button"
                                                onClick={handleSignIn}
                                            >
                                                Sign in
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div className="feedback-actions">
                                    <button
                                        type="button"
                                        onClick={handleClose}
                                        className="feedback-cancel-button"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting || !isAuthenticated}
                                        className="feedback-submit-button"
                                    >
                                        {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default FeedbackButton;
